import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Expand, X, PlayCircle, Rotate3d } from "lucide-react";
import { imgUrl } from "@/lib/api";

function Stage({ images, idx, go, alt, className, fit = "object-cover" }) {
  return (
    <motion.div className={`relative overflow-hidden ${className}`} drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.15}
      onDragEnd={(_, info) => { if (info.offset.x < -60) go(1); else if (info.offset.x > 60) go(-1); }}>
      <AnimatePresence initial={false}>
        <motion.img key={idx} src={imgUrl(images[idx])} alt={`${alt} — image ${idx + 1} of ${images.length}`} draggable={false}
          className={`absolute inset-0 h-full w-full select-none ${fit}`}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} />
      </AnimatePresence>
    </motion.div>
  );
}

const NavBtn = ({ dir, onClick, light, testid }) => (
  <button type="button" onClick={onClick} aria-label={dir < 0 ? "Previous image" : "Next image"} data-testid={testid}
    className={`absolute top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full backdrop-blur transition-colors ${dir < 0 ? "left-4" : "right-4"} ${light ? "bg-paper/10 text-paper hover:bg-paper hover:text-forest" : "bg-paper/80 text-forest hover:bg-paper"}`}>
    {dir < 0 ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
  </button>
);

function Thumbs({ images, idx, setIdx, dark }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current?.children?.[idx];
    if (el && ref.current) ref.current.scrollTo({ left: el.offsetLeft - ref.current.clientWidth / 2 + el.clientWidth / 2, behavior: "smooth" });
  }, [idx]);
  return (
    <div ref={ref} className="no-scrollbar flex gap-2 overflow-x-auto" data-lenis-prevent>
      {images.map((src, i) => (
        <button key={src + i} type="button" onClick={() => setIdx(i)} aria-label={`Show image ${i + 1}`} aria-current={i === idx} data-testid={`gallery-thumb-${i}`}
          className={`relative h-16 w-24 shrink-0 overflow-hidden transition-opacity sm:h-20 sm:w-28 ${i === idx ? "opacity-100 ring-2 ring-offset-2 " + (dark ? "ring-paper ring-offset-charcoal" : "ring-forest ring-offset-paper") : "opacity-60 hover:opacity-100"}`}>
          <img src={imgUrl(src)} alt="" loading="lazy" className="h-full w-full object-cover" />
        </button>
      ))}
    </div>
  );
}

export default function Gallery({ images = [], alt, videoUrl, viewerUrl }) {
  const list = images.length ? images : ["/img/hero.webp"];
  const [idx, setIdx] = useState(0);
  const [full, setFull] = useState(false);
  const [video, setVideo] = useState(false);
  const go = useCallback((d) => setIdx((i) => (i + d + list.length) % list.length), [list.length]);

  useEffect(() => {
    if (!full && !video) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { setFull(false); setVideo(false); }
      if (full && e.key === "ArrowRight") go(1);
      if (full && e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; window.__lenis?.start(); };
  }, [full, video, go]);

  return (
    <div data-testid="vehicle-gallery">
      <div className="group relative">
        <Stage images={list} idx={idx} go={go} alt={alt} className="aspect-[3/2] cursor-grab bg-stone active:cursor-grabbing" />
        <NavBtn dir={-1} onClick={() => go(-1)} testid="gallery-prev" />
        <NavBtn dir={1} onClick={() => go(1)} testid="gallery-next" />
        <div className="absolute bottom-4 left-4 bg-charcoal/70 px-3 py-1.5 text-xs font-semibold tracking-[0.14em] text-paper" data-testid="gallery-counter">{idx + 1} / {list.length}</div>
        <div className="absolute bottom-4 right-4 flex gap-2">
          {videoUrl && <button type="button" onClick={() => setVideo(true)} className="flex items-center gap-2 bg-paper/90 px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-forest" data-testid="gallery-video"><PlayCircle className="h-4 w-4" /> Video</button>}
          {viewerUrl && <a href={viewerUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-paper/90 px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-forest" data-testid="gallery-360"><Rotate3d className="h-4 w-4" /> 360°</a>}
          <button type="button" onClick={() => setFull(true)} className="flex items-center gap-2 bg-paper/90 px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-forest" data-testid="gallery-fullscreen"><Expand className="h-4 w-4" /> Fullscreen</button>
        </div>
      </div>
      <div className="mt-3"><Thumbs images={list} idx={idx} setIdx={setIdx} /></div>

      <AnimatePresence>
        {full && (
          <motion.div role="dialog" aria-modal="true" aria-label="Fullscreen gallery" data-testid="gallery-lightbox" data-lenis-prevent
            className="on-dark fixed inset-0 z-[80] flex flex-col bg-charcoal text-paper" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="flex items-center justify-between px-5 py-4">
              <p className="text-sm tracking-[0.14em]">{idx + 1} / {list.length}</p>
              <button type="button" onClick={() => setFull(false)} aria-label="Close gallery" data-testid="gallery-close"><X className="h-7 w-7" strokeWidth={1.5} /></button>
            </div>
            <div className="relative flex-1">
              <Stage images={list} idx={idx} go={go} alt={alt} className="h-full" fit="object-contain" />
              <NavBtn dir={-1} onClick={() => go(-1)} light testid="lightbox-prev" />
              <NavBtn dir={1} onClick={() => go(1)} light testid="lightbox-next" />
            </div>
            <div className="px-5 py-4"><Thumbs images={list} idx={idx} setIdx={setIdx} dark /></div>
          </motion.div>
        )}
        {video && (
          <motion.div role="dialog" aria-modal="true" aria-label="Vehicle video" className="fixed inset-0 z-[80] grid place-items-center bg-charcoal/95 p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <button type="button" onClick={() => setVideo(false)} aria-label="Close video" className="absolute right-5 top-4 text-paper"><X className="h-7 w-7" /></button>
            <iframe title="Vehicle video" src={videoUrl} className="aspect-video w-full max-w-5xl" allow="autoplay; fullscreen" allowFullScreen />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
